import { Box, Flex, Heading, Spinner, Text } from '@chakra-ui/react';
import { ChevronRight } from 'lucide-react';
import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { InternationalEventResponse } from 'api/responseTypes';
import { InternationalEventListItem } from 'components/InternationalEventListItem/InternationalEventListItem';
import { useEventTracking } from 'hooks/useEventTracking';
import {
  useInternationalEventsQuery,
  useIsInternationalEventsEnabled,
} from 'pages/InternationalEvents/hooks/useInternationalEvents';
import { ROUTES } from 'router/routes';

const CONTENT_MAX_W = { base: '100%', md: '720px' };

/**
 * All events for a single country/city — reached from a country section's "عرض الكل".
 * A flat vertical compact list with a header, count, and back navigation.
 */
export const InternationalEventsCountryPage = () => {
  const navigate = useNavigate();
  const { country: countryParam } = useParams();
  const country = countryParam ? decodeURIComponent(countryParam) : '';
  const { eventTracking } = useEventTracking();
  const { isLoading, items } = useInternationalEventsQuery();
  const isEnabled = useIsInternationalEventsEnabled();

  useEffect(() => {
    eventTracking.page.viewed({
      pageName: 'international_events_country',
      url: window.location.href,
    });
  }, [eventTracking]);

  if (!isEnabled) {
    return <Navigate to={ROUTES.home} replace />;
  }

  const countryEvents = items.filter((event) => (event.country ?? '') === country);

  const handleItemClick = (event: InternationalEventResponse) => {
    eventTracking.internationalEvent.clicked({
      eventId: event.id,
      eventName: event.title,
      eventDate: event.date ?? null,
      venue: event.city ?? undefined,
      source: 'international_events_page',
    });
    navigate('/international-event/' + event.id);
  };

  return (
    <Box bg='white' minH='100vh' py={{ base: 6, md: 10 }} dir='rtl'>
      <Box maxW={CONTENT_MAX_W} mx='auto' px={{ base: 5, md: 8 }}>
        {/* Back */}
        <Flex
          as='button'
          type='button'
          onClick={() => navigate(-1)}
          align='center'
          gap={1}
          mb={4}
          color='dark.D900'
          aria-label='رجوع'
        >
          <ChevronRight size={20} />
          <Text textStyle='bodyMediumRegular'>
            <FormattedMessage defaultMessage='رجوع' id='internationalEvents.back' />
          </Text>
        </Flex>

        {/* Header: country + count */}
        <Flex align='baseline' gap={2} mb={{ base: 5, md: 6 }}>
          <Heading as='h1' variant={{ base: 'headingMedium', md: 'headingLarge' }}>
            <FormattedMessage
              defaultMessage='فعاليات {country}'
              id='internationalEvents.countryEvents'
              values={{ country }}
            />
          </Heading>
          {!isLoading && (
            <Text textStyle='bodyMediumRegular' color='dark.D500'>
              ({countryEvents.length})
            </Text>
          )}
        </Flex>

        {isLoading ? (
          <Flex align='center' justify='center' py={16}>
            <Spinner color='green.G50' size='xl' thickness='4px' />
          </Flex>
        ) : countryEvents.length === 0 ? (
          <Flex align='center' justify='center' py={16}>
            <Text textStyle='bodyLargeRegular' color='dark.D500' textAlign='center'>
              <FormattedMessage
                defaultMessage='لا توجد فعاليات متاحة'
                id='homepage.events.noEvents'
              />
            </Text>
          </Flex>
        ) : (
          <Box
            as='ul'
            listStyleType='none'
            display='flex'
            flexDirection='column'
            gap={5}
            m={0}
            p={0}
          >
            {countryEvents.map((event) => (
              <InternationalEventListItem
                key={event.id}
                event={event}
                onCardClick={() => handleItemClick(event)}
              />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};
